import clsx from 'clsx'
import {
  type ComponentPropsWithoutRef,
  type ForwardedRef,
  forwardRef,
  type PropsWithChildren,
  useMemo,
} from 'react'
import type { NavLinkProps } from 'react-router'

import { FvNavLink } from './FvNav.jsx'
import { Icon, type IconProps } from './Icon.jsx'

type FvButtonTheme =
  | 'callout'
  | 'default'
  | 'plain'
  | 'primary'
  | 'search'
  | 'secondary'
  | 'round'
  | 'underlined'
  | 'danger'
  | 'cancel'
  | 'white'
  | 'pagination'

export type FvLinkButtonProps = Omit<
  ComponentPropsWithoutRef<'a'> &
    Partial<Pick<NavLinkProps, 'to' | 'end'>> &
    Partial<Pick<IconProps, 'icon' | 'transform'>> & {
      disabled?: boolean
      iconClass?: string
      buttonTextClass?: string
      theme?: FvButtonTheme
      active?: boolean
    },
  'className'
> & {
  className?: string | ((props: { isActive: boolean }) => string)
}

export const FvLinkButton = forwardRef(
  (
    {
      disabled,
      onClick,
      href = '#',
      to,
      icon,
      children,
      iconClass,
      buttonTextClass,
      transform,
      theme,
      active,
      ...props
    }: FvLinkButtonProps,
    ref?: ForwardedRef<HTMLAnchorElement>,
  ) => {
    let anchorClick = onClick

    if (href === '#') {
      anchorClick = e => {
        e.preventDefault()
        onClick && onClick(e)
      }
    }

    const elContents = (
      <>
        {icon && (
          <Icon
            className={clsx(iconClass)}
            {...{
              icon,
              transform,
            }}
          />
        )}
        {children && (
          <span
            className={clsx('items-center overflow-hidden', buttonTextClass)}
          >
            {children}
          </span>
        )}
      </>
    )

    const themeClass = useMemo(() => (theme ? themes[theme] : ''), [theme])
    const activeClass = useMemo(
      () => (active && theme ? activeThemes[theme] : ''),
      [active, theme],
    )
    const baseClassName =
      'whitespace-nowrap hover:[&>svg]:text-[#333] leading-none'
    const className = clsx(
      typeof props.className === 'string' ? props.className : '',
      themeClass,
      activeClass,
      {
        'pointer-events-none': disabled || active,
      },
    )

    return to ? (
      <FvNavLink
        {...props}
        className={({ isActive }) =>
          clsx(
            baseClassName,
            typeof props.className === 'string'
              ? className
              : [props.className?.({ isActive }), themeClass, activeClass],
          )
        }
        onClick={onClick}
        to={to}
      >
        {elContents}
      </FvNavLink>
    ) : (
      <a
        {...props}
        className={clsx(baseClassName, className)}
        href={href}
        onClick={anchorClick}
        ref={ref}
        role="button"
      >
        {elContents}
      </a>
    )
  },
)

export type FvButtonProps = PropsWithChildren<
  ComponentPropsWithoutRef<'button'> & {
    icon?: IconProps['icon']
    loading?: boolean
    theme?: FvButtonTheme
    transform?: IconProps['transform']
    size?: IconProps['size']
    type?: 'button' | 'reset' | 'submit'
    iconClass?: string
    buttonTextClass?: string
    fwd?: boolean
    activeHint?: boolean
    active?: boolean
  }
>

export const FvButton = forwardRef(
  (props: FvButtonProps, ref?: ForwardedRef<HTMLButtonElement>) => {
    const { theme, ...otherProps } = props

    return (
      <IconButton
        ref={ref}
        {...otherProps}
        theme={theme}
        className={clsx(
          'btn whitespace-nowrap leading-none hover:z-10',
          props.className,
        )}
      />
    )
  },
)

const themes: Record<FvButtonTheme, string> = {
  default: `bg-fv-blue-50 h-[2.8rem] px-[0.6rem] border border-fv-blue hover:border-[#999] relative hover:[&>svg]:text-[#333] outline-2 outline-dotted outline-transparent outline-offset-0 transition-all hover:outline-offset-2 hover:outline-fv-gray-500 flex items-center fa-width-auto`,

  search: `bg-fv-green-50 h-[2.8rem] px-[0.6rem] border border-fv-green hover:border-[#999] relative hover:[&>svg]:text-[#333] outline-2 outline-dotted outline-transparent outline-offset-0 transition-all hover:outline-offset-2 hover:outline-fv-gray-500 flex items-center fa-width-auto`,

  white: `bg-white h-[2.8rem] px-[0.6rem] border border-fv-blue hover:border-[#999] relative hover:[&>svg]:text-[#333] outline-2 outline-dotted outline-transparent outline-offset-0 transition-all hover:outline-offset-2 hover:outline-fv-gray-500 flex items-center fa-width-auto`,

  callout: 'text-fv-blue hover:text-[#333]',

  plain: `bg-none border-none p-[0.6rem] hover:[&>svg]:text-[#333] h-[2.8rem]`,

  primary: `flex items-center bg-fv-blue text-[#f2f2f2] border border-fv-blue h-[2.8rem] px-[0.6rem] relative [&>svg]:text-[#fffde9] outline-2 outline-dotted outline-transparent outline-offset-0 transition-all hover:outline-offset-2 hover:outline-fv-blue-500 hover:[&>svg]:text-[#333] fa-width-auto`,

  secondary: `bg-fv-blue-100 h-[2.8rem] relative px-[0.6rem] border border-fv-blue  relative hover:[&>svg]:text-[#333] flex items-center fa-width-auto`,

  underlined: `underline decoration-fv-blue underline-offset-4 hover:decoration-inherit`,

  round: `border-fv-blue bg-fv-blue-100 relative flex h-4 w-4 items-center justify-center rounded-full border-2 p-4 hover:[&>svg]:text-[#333] fa-width-auto`,

  danger: `flex items-center bg-fv-orange-50 border border-fv-orange-300 h-[2.8rem] px-[0.6rem] relative [&>svg]:text-fv-orange-bright outline-2 outline-dotted outline-transparent outline-offset-0 transition-all hover:outline-offset-2 hover:outline-fv-red-400 hover:[&>svg]:text-[#333] fa-width-auto`,

  pagination: `bg-fv-blue-50 rounded-xs leading-none px-[.3rem] py-[.1rem] border border-fv-blue hover:border-[#999] relative hover:[&>svg]:text-[#333] outline-2 outline-dotted outline-transparent outline-offset-0 hover:outline-offset-2 hover:outline-fv-gray-500 flex items-center`,

  cancel: `bg-none border-none p-[0.6rem] hover:[&>svg]:text-[#333] h-[2.8rem] fa-width-auto`,
} as const

const activeThemes: Partial<Record<FvButtonTheme, string>> = {
  pagination: 'bg-fv-gray-dark border-fv-gray-dark text-white',
} as const

export const IconButton = forwardRef(
  (
    {
      icon,
      type = 'button',
      className,
      children,
      theme,
      loading = false,
      onClick,
      transform,
      size,
      iconClass,
      buttonTextClass,
      fwd,
      disabled,
      activeHint,
      active,

      ...props
    }: PropsWithChildren<FvButtonProps>,
    ref?: ForwardedRef<HTMLButtonElement>,
  ) => {
    const themeClass = useMemo(() => (theme ? themes[theme] : ''), [theme])
    const activeClass = useMemo(
      () => (active && theme ? activeThemes[theme] : ''),
      [active, theme],
    )

    // When theme is 'cancel', always use 'times' icon
    if (theme === 'cancel') {
      icon = 'times'
    }

    transform = transform || undefined

    return (
      <button
        ref={ref}
        className={clsx(
          themeClass,
          activeClass,
          className,
          fwd &&
            'before:absolute before:right-[-8px] before:top-1/2 before:z-20 before:-translate-y-1/2 before:border-[8px] before:border-r-0 before:border-b-transparent before:border-t-transparent after:absolute after:right-[-9px] after:top-1/2 after:z-10 after:-translate-y-1/2 after:border-[8px] after:border-r-0 after:border-b-transparent  after:border-t-transparent',
          fwd &&
            theme === 'default' &&
            'before:border-l-fv-blue-50 after:border-l-fv-blue',
          fwd &&
            theme === 'primary' &&
            'before:border-l-fv-blue after:border-l-fv-blue',
          fwd &&
            theme === 'secondary' &&
            'before:border-l-fv-blue-100 after:border-l-fv-blue',
          fwd &&
            theme === 'danger' &&
            'before:border-l-red-500 after:border-l-red-500',

          { 'opacity-50 hover:[&>svg]:text-inherit': disabled },
          { 'pointer-events-none': disabled || active },
          !theme && 'hover:[&>svg]:text-[#333]',
        )}
        type={type}
        onClick={e => !loading && !disabled && onClick?.(e)}
        disabled={disabled}
        {...props}
      >
        {loading && <Icon icon="spinner" transform={transform} />}

        {icon && !loading && (
          <Icon
            icon={icon}
            transform={transform}
            size={size}
            className={clsx(iconClass, {
              'text-fv-green': theme === 'search',
            })}
          />
        )}
        {children && (
          <span
            className={clsx(
              'relative',
              buttonTextClass,
              !theme &&
                activeHint &&
                'inline-block after:absolute after:w-[.4rem] after:h-[.4rem] after:rounded-full after:bg-fv-orange after:-bottom-[.85rem] after:left-1/2',
            )}
          >
            {children}
          </span>
        )}
      </button>
    )
  },
)

export const isFvLinkButtonProps = (
  props: FvLinkButtonProps | FvButtonProps,
): props is FvLinkButtonProps => {
  const val = props as FvLinkButtonProps
  return !!(val.to || val.href)
}
